import React from 'react';
import {
  CG_BOOST_METRIC_PROVENANCE,
  CgBoostMetricKey,
  CgBoostPlatform,
  getPlatforms,
  PLATFORM_LABELS,
  PlatformBreakdownEntry,
  PlatformsPayload,
} from '../../../services/cgBoostApi';
import { ViewDataBoundary } from '../useViewData';
import MetricLabel from '../MetricLabel';
import { cardStyle, eyebrowLabel, flagToStatus, sectionTitle } from '../styles';
import { StatusPill } from '../../ui';

function provenanceFor(metric: CgBoostMetricKey, platform: CgBoostPlatform): string | undefined {
  const entry = CG_BOOST_METRIC_PROVENANCE[metric];
  return entry?.[platform] ?? entry?.derived;
}

interface Props { campaignId: string; }

const PlatformsView: React.FC<Props> = ({ campaignId }) => (
  <ViewDataBoundary
    loader={getPlatforms}
    campaignId={campaignId}
    loadingLabel="Loading platform comparison…"
    render={(data) => <PlatformsBody data={data} />}
  />
);

const PlatformsBody: React.FC<{ data: PlatformsPayload }> = ({ data }) => {
  const flagPlatform = (p: CgBoostPlatform | null) => (p ? PLATFORM_LABELS[p] : '—');

  return (
    <>
      <div style={cardStyle}>
        <div style={sectionTitle}>Automated Flags</div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <FlagCard
            icon="🏆"
            title="Top Attention Platform"
            detail="Platform with highest VTR"
            value={flagPlatform(data.flags.topAttention)}
          />
          <FlagCard
            icon="💰"
            title="Most Efficient Distribution"
            detail="Platform with lowest CPM"
            value={flagPlatform(data.flags.mostEfficient)}
          />
          <FlagCard
            icon="🎯"
            title="Strongest Response"
            detail="Platform with highest CTR"
            value={flagPlatform(data.flags.strongestResponse)}
          />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
        {data.perPlatform.map((row) => (
          <PlatformColumn key={row.platform} row={row} flags={data.flags} />
        ))}
      </div>
    </>
  );
};

const FlagCard: React.FC<{ icon: string; title: string; detail: string; value: string }> = ({
  icon, title, detail, value,
}) => (
  <div style={{
    flex: 1, minWidth: 220, padding: 14, borderRadius: 10,
    border: '1px solid var(--border-1)', background: 'var(--bg-app)',
  }}>
    <div style={{ fontSize: 18 }}>{icon}</div>
    <div style={{ ...eyebrowLabel, marginTop: 6 }}>{title}</div>
    <div style={{ fontSize: 11, color: 'var(--fg-3)', marginTop: 2 }}>{detail}</div>
    <div style={{ fontSize: 18, fontWeight: 700, marginTop: 8 }}>{value}</div>
  </div>
);

const PlatformColumn: React.FC<{
  row: PlatformBreakdownEntry;
  flags: { topAttention: CgBoostPlatform | null; mostEfficient: CgBoostPlatform | null; strongestResponse: CgBoostPlatform | null };
}> = ({ row, flags }) => {
  const m = row.metrics;
  const isTopAttn = flags.topAttention === row.platform;
  const isEfficient = flags.mostEfficient === row.platform;
  const isStrongest = flags.strongestResponse === row.platform;

  return (
    <div style={{ ...cardStyle, display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontSize: 16, fontWeight: 700 }}>{PLATFORM_LABELS[row.platform]}</div>
        <div style={{ fontSize: 11, color: 'var(--fg-3)' }}>
          {(row.shareOfReachPct * 100).toFixed(1)}% of total reach
        </div>
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {isTopAttn && <StatusPill variant={flagToStatus.top} noDot size="sm">Top Attention</StatusPill>}
        {isEfficient && <StatusPill variant={flagToStatus.info} noDot size="sm">Most Efficient</StatusPill>}
        {isStrongest && <StatusPill variant={flagToStatus.top} noDot size="sm">Strongest Response</StatusPill>}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <MetricLabel metric="impressions" value={m.impressions} size="sm" tooltip={provenanceFor('impressions', row.platform)} />
        <MetricLabel metric="views" value={m.views} size="sm" tooltip={provenanceFor('views', row.platform)} />
        <MetricLabel metric="vtr" value={m.vtr} size="sm" tooltip={provenanceFor('vtr', row.platform)} />
        <MetricLabel metric="hookRate" value={m.hookRate} size="sm" tooltip={provenanceFor('hookRate', row.platform)} />
        <MetricLabel metric="completionRate" value={m.completionRate} size="sm" tooltip={provenanceFor('completionRate', row.platform)} />
        <MetricLabel metric="ctr" value={m.ctr} size="sm" tooltip={provenanceFor('ctr', row.platform)} />
        <MetricLabel metric="cpm" value={m.cpm} size="sm" tooltip={provenanceFor('cpm', row.platform)} />
        <MetricLabel metric="cpv" value={m.cpv} size="sm" tooltip={provenanceFor('cpv', row.platform)} />
        <MetricLabel metric="frequency" value={m.frequency} size="sm" tooltip={provenanceFor('frequency', row.platform)} />
      </div>
      <div style={{ marginTop: 4, padding: 12, borderRadius: 8, background: 'var(--bg-app)', border: '1px solid var(--border-1)' }}>
        <div style={eyebrowLabel}>Budget Deployment Rate</div>
        <div style={{ fontSize: 14, fontWeight: 600, marginTop: 4 }}>
          ${Math.round(row.spendPacing.spend).toLocaleString()} / ${row.spendPacing.planned.toLocaleString()} ({(row.spendPacing.pctDeployed * 100).toFixed(1)}%)
        </div>
      </div>
    </div>
  );
};

export default PlatformsView;
